import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";

/**
 * SiteNav — Founder plain-ink 2026-02-15.
 *
 * Nav order: Games · Products (dropdown) · What We Collapse · Artists
 * (Our Artists) · Philanthropy · Studio (dropdown).
 */
const STUDIO_LINKS = [
  { to: "/about", label: "About" },
  { to: "/mission", label: "Mission" },
  { to: "/what-we-do", label: "What We Do" },
  { to: "/contact", label: "Contact" },
  { to: "/philanthropy", label: "Philanthropy" },
  { to: "/legal", label: "Legal" },
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
];

export default function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // "products" | "studio" | null
  const [productLinks, setProductLinks] = useState([]);
  const closeTimer = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        const groups = r.data.products || [];
        setProductLinks(
          groups.map((g) => ({ to: `/products/${g.slug}`, label: g.name }))
        );
      })
      .catch(() => {});
  }, []);

  const openHover = (key) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  const closeHover = () => {
    closeTimer.current = setTimeout(() => setOpenMenu(null), 140);
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        scrolled
          ? "bg-[#050505]/85 backdrop-blur-md border-b border-white/5"
          : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-3">
          <img
            src="/art/aegishjalmr.png"
            alt="Ægishjálmur — the Helm of Awe"
            data-testid="nav-logo-glyph"
            width={36}
            height={36}
            className="w-8 h-8 sm:w-9 sm:h-9 object-contain drop-shadow-[0_0_12px_rgba(255,90,0,0.35)] transition-transform duration-500 group-hover:rotate-[22.5deg]"
          />
          <span className="text-lg sm:text-xl tracking-[0.28em] uppercase text-[#ededed] group-hover:text-[#ff5a00] transition-colors">
            Resilience<span className="text-[#ff5a00]"> Studios</span>
          </span>
        </Link>

        {/* Center nav — desktop */}
        <nav className="hidden md:flex items-center gap-9">
          <NavLink
            to="/games"
            data-testid="nav-games"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                isActive ? "text-[#ff5a00]" : "text-[#a1a1aa] hover:text-[#ededed]"
              }`
            }
          >
            Games
          </NavLink>

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => openHover("products")}
            onMouseLeave={closeHover}
          >
            <NavLink
              to="/products"
              data-testid="nav-products"
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                  isActive || openMenu === "products"
                    ? "text-[#ff5a00]"
                    : "text-[#a1a1aa] hover:text-[#ededed]"
                }`
              }
            >
              Products
            </NavLink>
            <AnimatePresence>
              {openMenu === "products" && productLinks.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}
                  data-testid="nav-products-dropdown"
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[280px]"
                >
                  <div className="bg-[#0a0a0c] border border-white/10 shadow-2xl">
                    {productLinks.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        data-testid={`nav-products-dd-${l.to.split("/").pop()}`}
                        className="block px-5 py-3 text-[11px] uppercase tracking-[0.24em] text-[#a1a1aa] hover:text-[#ff5a00] hover:bg-white/5 border-b border-white/5 last:border-b-0 transition-colors"
                      >
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink
            to="/what-we-collapse"
            data-testid="nav-what-we-collapse"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                isActive ? "text-[#ff5a00]" : "text-[#a1a1aa] hover:text-[#ededed]"
              }`
            }
          >
            What We Collapse
          </NavLink>

          <NavLink
            to="/artists"
            data-testid="nav-artists"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                isActive ? "text-[#ff5a00]" : "text-[#a1a1aa] hover:text-[#ededed]"
              }`
            }
          >
            Our Artists
          </NavLink>

          <NavLink
            to="/philanthropy"
            data-testid="nav-philanthropy"
            className={({ isActive }) =>
              `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                isActive ? "text-[#ff5a00]" : "text-[#a1a1aa] hover:text-[#ededed]"
              }`
            }
          >
            Philanthropy
          </NavLink>

          {/* Studio dropdown */}
          <div
            className="relative"
            onMouseEnter={() => openHover("studio")}
            onMouseLeave={closeHover}
          >
            <NavLink
              to="/about"
              data-testid="nav-studio"
              className={({ isActive }) =>
                `text-[11px] uppercase tracking-[0.28em] transition-colors ${
                  isActive || openMenu === "studio"
                    ? "text-[#ff5a00]"
                    : "text-[#a1a1aa] hover:text-[#ededed]"
                }`
              }
            >
              Studio
            </NavLink>
            <AnimatePresence>
              {openMenu === "studio" && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}
                  data-testid="nav-studio-dropdown"
                  className="absolute right-0 top-full pt-4 w-[220px]"
                >
                  <div className="bg-[#0a0a0c] border border-white/10 shadow-2xl">
                    {STUDIO_LINKS.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        data-testid={`nav-studio-dd-${l.to.slice(1)}`}
                        className="block px-5 py-3 text-[11px] uppercase tracking-[0.24em] text-[#a1a1aa] hover:text-[#ff5a00] hover:bg-white/5 border-b border-white/5 last:border-b-0 transition-colors"
                      >
                        {l.label}
                      </NavLink>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* Mobile toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Open menu"
            className="md:hidden text-[#ededed] hover:text-[#ff5a00] transition-colors text-[10px] uppercase tracking-[0.28em]"
          >
            {mobileOpen ? "Close" : "Menu"}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            data-testid="nav-mobile-drawer"
            className="md:hidden bg-[#0a0a0c] border-t border-white/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="px-5 py-6 space-y-4">
              <NavLink to="/games" data-testid="nav-games-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed]">
                Games
              </NavLink>

              <div>
                <NavLink to="/products" data-testid="nav-products-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed] mb-2">
                  Products
                </NavLink>
                <div className="pl-4 space-y-2 border-l border-white/10">
                  {productLinks.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-products-dd-${l.to.split("/").pop()}-mobile`}
                      className="block text-[10px] uppercase tracking-[0.28em] text-[#a1a1aa] hover:text-[#ff5a00]"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>

              <NavLink to="/what-we-collapse" data-testid="nav-what-we-collapse-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed]">
                What We Collapse
              </NavLink>
              <NavLink to="/artists" data-testid="nav-artists-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed]">
                Our Artists
              </NavLink>
              <NavLink to="/philanthropy" data-testid="nav-philanthropy-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed]">
                Philanthropy
              </NavLink>

              <div>
                <NavLink to="/about" data-testid="nav-studio-mobile" className="block text-xs uppercase tracking-[0.32em] text-[#ededed] mb-2">
                  Studio
                </NavLink>
                <div className="pl-4 space-y-2 border-l border-white/10">
                  {STUDIO_LINKS.map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-studio-dd-${l.to.slice(1)}-mobile`}
                      className="block text-[10px] uppercase tracking-[0.28em] text-[#a1a1aa] hover:text-[#ff5a00]"
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
